import React, { useRef, useState, useEffect } from 'react';
import { Bug, Map, BarChart, Bell, Cpu, Camera } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import appleStoreQR from '../../assets/qr_pestid_applestore.svg';
import playStoreQR from '../../assets/qr_pestid_playstore.svg';
import consoleBackground from '../../assets/console.png';
import pestidLogo from '../../assets/pestidlogo.webp';
import invertedLogo from '../../assets/Inverted_logo.png';
import appleLogo from '../../assets/apple-logo-svgrepo-com.svg';
import googleLogo from '../../assets/google-icon-logo-svgrepo-com.svg';

const PestIDA5Banner = () => {
  const bannerRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({
    background: null,
    pestidLogo: null,
    invertedLogo: null,
    appleStoreQR: null,
    playStoreQR: null,
    appleLogo: null,
    googleLogo: null
  });
  
  // A5 dimensions: 148 × 210 mm (landscape orientation: 210 x 148 mm)
  const bannerWidthMm = 210; // Landscape orientation
  const bannerHeightMm = 148;
  const bannerAspectRatio = bannerWidthMm / bannerHeightMm;
  
  // Feature data with updated titles
  const features = [
    {
      title: "Instant Pest ID",
      icon: <Bug size={28} className="text-teal-400" strokeWidth={1.5} style={{ color: 'rgb(64, 224, 208)' }} />
    },
    {
      title: "Real-Time Alerts",
      icon: <Bell size={28} className="text-blue-400" strokeWidth={1.5} />
    },
    {
      title: "Species Mapping",
      icon: <Map size={28} className="text-purple-400" strokeWidth={1.5} />
    },
    {
      title: "Automated Sorting",
      icon: <Cpu size={28} className="text-green-400" strokeWidth={1.5} />
    },
    {
      title: "Actionable Insights",
      icon: <BarChart size={28} className="text-orange-400" strokeWidth={1.5} />
    },
    {
      title: "Simple Photo Reporting",
      icon: <Camera size={28} className="text-pink-400" strokeWidth={1.5} />
    }
  ];

  // Preload images on component mount
  useEffect(() => {
    const preloadImages = async () => {
      try {
        setProgress(10);
        
        // Helper function to load an image as a data URL
        const loadImage = (src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = (err) => reject(err);
            img.src = src;
          });
        };
        
        // Load all required images
        const [backgroundDataUrl, pestidLogoDataUrl, invertedLogoDataUrl, appleStoreQRDataUrl, playStoreQRDataUrl, appleLogoDataUrl, googleLogoDataUrl] = await Promise.all([
          loadImage(consoleBackground),
          loadImage(pestidLogo),
          loadImage(invertedLogo),
          loadImage(appleStoreQR),
          loadImage(playStoreQR),
          loadImage(appleLogo),
          loadImage(googleLogo)
        ]);
        
        // Store loaded images
        setPreloadedImages({
          background: backgroundDataUrl,
          pestidLogo: pestidLogoDataUrl,
          invertedLogo: invertedLogoDataUrl,
          appleStoreQR: appleStoreQRDataUrl,
          playStoreQR: playStoreQRDataUrl,
          appleLogo: appleLogoDataUrl,
          googleLogo: googleLogoDataUrl
        });
        
        setImagesLoaded(true);
        setProgress(0);
      } catch (error) {
        console.error('Failed to preload images:', error);
        // Continue anyway, we'll use the original images
        setImagesLoaded(true);
        setProgress(0);
      }
    };
    
    preloadImages();
  }, []);

  // Toggle preview mode (for full-size preview)
  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
    // Scroll to top when entering preview mode
    if (!isPreviewMode) {
      window.scrollTo(0, 0);
    }
  };

  // Handle export to PNG using html-to-image
  const handleExportPNG = async () => {
    if (!bannerRef.current || isExporting) return;
    
    try {
      setIsExporting(true);
      setProgress(20);

      // If we're not in preview mode, enter it first
      if (!isPreviewMode) {
        setIsPreviewMode(true);
        // Wait for component to update before taking screenshot
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setProgress(40);
      
      // Use toPng to capture the banner
      const dataUrl = await toPng(bannerRef.current, {
        quality: 1.0,
        pixelRatio: 3, // Higher scale for better quality
        cacheBust: true,
        backgroundColor: '#000000', // Ensure black background
        style: {
          margin: 0,
          padding: 0,
        },
      });
      
      setProgress(70);
      
      // Create download link
      const link = document.createElement('a');
      link.download = 'pestid-a5-banner.png';
      link.href = dataUrl;
      link.click();
      
      setProgress(100);
      
      // Reset
      setTimeout(() => {
        setProgress(0);
        setIsExporting(false);
      }, 1000);
    } catch (error) {
      console.error('PNG export error:', error);
      alert('Failed to export PNG: ' + error.message);
      setIsExporting(false);
      setProgress(0);
    }
  };

  // Handle export to PDF
  const handleExportPDF = async () => {
    if (!bannerRef.current || isExporting) return;
    
    try {
      setIsExporting(true);
      setProgress(20);
      
      // If we're not in preview mode, enter it first
      if (!isPreviewMode) {
        setIsPreviewMode(true);
        // Wait for component to update before taking screenshot
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setProgress(40);
      
      // Use toPng to capture the banner
      const dataUrl = await toPng(bannerRef.current, {
        quality: 1.0,
        pixelRatio: 3, // Higher scale for better quality
        cacheBust: true,
        backgroundColor: '#000000', // Ensure black background
        style: {
          margin: 0,
          padding: 0,
        },
      });
      
      setProgress(60);
      
      // Create PDF with A5 dimensions (landscape)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a5',
        compress: true
      });
      
      // Add image to PDF
      pdf.addImage(dataUrl, 'PNG', 0, 0, bannerWidthMm, bannerHeightMm);
      
      setProgress(80);
      
      // Save PDF
      pdf.save('pestid-a5-banner.pdf');
      
      setProgress(100);
      
      // Reset
      setTimeout(() => {
        setProgress(0);
        setIsExporting(false);
      }, 1000);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF: ' + error.message);
      setIsExporting(false);
      setProgress(0);
    }
  };
  
  // Reset preview mode when component unmounts
  useEffect(() => {
    return () => {
      setIsPreviewMode(false);
    };
  }, []);
  
  // If images are still loading, show a loading indicator
  if (!imagesLoaded) {
    return (
      <div className="w-full bg-gray-200 min-h-screen flex flex-col items-center justify-center">
        <div className="mb-4 text-xl font-medium">Preparing banner...</div>
        <div className="w-64 bg-gray-300 h-3 rounded-full overflow-hidden">
          <div className="bg-blue-600 h-full" style={{ width: `${progress || 30}%` }}></div>
        </div>
        <p className="mt-4 text-gray-600">Loading images for optimal export quality</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-200 min-h-screen flex flex-col items-center py-8">
      {/* Controls - only show when not in preview mode */}
      {!isPreviewMode && (
        <div className="w-full max-w-4xl mb-8 px-4">
          <h1 className="text-2xl font-bold mb-4">PestID A5 Landscape Banner (210×148mm)</h1>
          <div className="flex flex-wrap gap-4 mb-4">
            <button 
              onClick={togglePreviewMode}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 transition-colors"
            >
              Preview Full Size
            </button>
            <button 
              onClick={handleExportPNG}
              disabled={isExporting}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Export as PNG
            </button>
            <button 
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-6 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Export as PDF
            </button>
          </div>
          <p className="mt-2 text-gray-600">
            A5 Banner size: {bannerWidthMm} × {bannerHeightMm}mm - Aspect ratio: {bannerAspectRatio.toFixed(3)}
          </p>
          
          {/* Progress bar for exporting */}
          {progress > 0 && (
            <div className="mt-2">
              <div className="w-full bg-gray-300 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-blue-600 h-full transition-all duration-300 ease-out" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {isExporting ? `Exporting: ${progress}% complete` : ''}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Exit preview button - only show when in preview mode */}
      {isPreviewMode && !isExporting && (
        <div className="fixed top-4 right-4 z-50">
          <button 
            onClick={togglePreviewMode}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors shadow-lg"
          >
            Exit Preview
          </button>
        </div>
      )}
      
      {/* The A5 banner itself */}
      <div 
        ref={bannerRef}
        className={`bg-black text-white ${!isPreviewMode ? 'border-2 border-teal-500' : ''}`}
        style={{ 
          width: isPreviewMode ? `${bannerWidthMm * 3}px` : '100%',
          maxWidth: isPreviewMode ? `${bannerWidthMm * 3}px` : '600px',
          aspectRatio: `${bannerAspectRatio}`,
          margin: '0 auto',
        }}
      >
        {/* Left side - Background with hexagonal pattern similar to the reference image */}
        <div className="w-1/2 h-full relative float-left">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `url(${preloadedImages.background || consoleBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.75
            }}
          ></div>
          
          {/* Logo in top left - Inverted logo - Adjusted position to match right logo */}
          <div className="absolute top-3 left-6 flex items-center z-10">
            <img 
              src={preloadedImages.invertedLogo || invertedLogo} 
              alt="ISD.ai Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          {/* PestID logo - Adjusted position to align with the transparent box */}
          <div className="absolute top-3 right-6 z-10">
            <img 
              src={preloadedImages.pestidLogo || pestidLogo} 
              alt="PestID Logo" 
              className="h-14 w-auto rounded-md overflow-hidden"
            />
          </div>
          
          {/* Title and Subtitle - with transparent box aligned with logo */}
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
            <div 
              className="bg-black bg-opacity-70 p-3 rounded-lg mx-6" 
              style={{ marginTop: '40px' }}
            >
              <h1 className="text-4xl font-bold mb-1 text-white text-center">PestID</h1>
              <p className="text-sm text-white mb-1 text-center">
                AI-powered biosecurity monitoring for tracking invasive species<br/>
                Protecting Australia's agriculture and ecosystems
              </p>
            </div>
            
            {/* QR CODES - Adjusted spacing */}
            <div className="mt-5 flex space-x-8 justify-center">
              {/* App Store QR Code */}
              <div className="flex flex-col items-center">
                <div className="bg-white p-2 rounded-md w-24 h-24 flex items-center justify-center shadow-md">
                  <img 
                    src={preloadedImages.appleStoreQR || appleStoreQR} 
                    alt="Download on App Store" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-2 w-8 h-8">
                  <img 
                    src={preloadedImages.appleLogo || appleLogo} 
                    alt="Apple" 
                    className="w-full h-full object-contain" 
                  />
                </div>
              </div>

              {/* Google Play QR Code */}
              <div className="flex flex-col items-center">
                <div className="bg-white p-2 rounded-md w-24 h-24 flex items-center justify-center shadow-md">
                  <img 
                    src={preloadedImages.playStoreQR || playStoreQR} 
                    alt="Get it on Google Play" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-2 w-8 h-8">
                  <img 
                    src={preloadedImages.googleLogo || googleLogo} 
                    alt="Google" 
                    className="w-full h-full object-contain" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - Features */}
        <div className="w-1/2 h-full bg-black flex flex-col float-left">
          {/* Title - Adjusted with more top padding */}
          <div className="pt-4 pb-2 px-2">
            <h2 className="text-xl font-bold text-center tracking-wider text-white">Key Features</h2>
            <p className="text-center text-white text-xs mt-0.5 mb-0">Smarter Biosecurity with AI + Citizen Science</p>
          </div>
          
          {/* Feature grid - IMPROVED: Fixed equal height boxes with proper sizing */}
          <div className="grid grid-cols-2 gap-3 px-3 py-3 flex-1">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-gray-900 rounded-md p-3 flex flex-col items-center justify-center border border-gray-800"
                style={{ height: '100px' }} // Further increased height for better visual presence
              >
                <div className="mb-2">
                  {feature.icon}
                </div>
                <div className="text-sm text-center font-medium">
                  {feature.title}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ clear: 'both' }}></div>
      </div>
    </div>
  );
};

export default PestIDA5Banner;